import { useMemo } from 'react';
import type { SelectProps, SelectValueProps } from '@radix-ui/react-select';
import { useFormik } from 'formik';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@antimatterhq/ui/components/select';
import { Text } from '@antimatterhq/ui/components/typography';
import { FlagSet } from '@antimatterhq/ui/utils/flags';
import { useFlags } from 'launchdarkly-react-client-sdk';

export type ExportDestinationDropdownProps = {
  name: string;
  onBlur: ReturnType<typeof useFormik>['handleBlur'];
  onChange: ReturnType<typeof useFormik>['handleChange'];
} & Pick<SelectProps, 'value' | 'disabled'> &
  Pick<SelectValueProps, 'id'>;

const OPTIONS = [
  { key: 'none', label: 'None (no export configured)' },
  { key: 'webhook', label: 'Webhook (post JSON to a url)' },
  { key: 'slack', label: 'Slack (post as a slack message)' },
  { key: 'jira', label: 'Jira (create a jira issue)' },
  { key: 'servicenow', label: 'ServiceNow (create a ServiceNow incident)' },
];

const Destination = ({ label }: { label?: string }) => {
  return <Text>{label}</Text>;
};

export default function ExportDestinationDropdown({
  value,
  onChange,
  onBlur,
  name,
  id,
  ...props
}: ExportDestinationDropdownProps) {
  const { enableJiraExports } = useFlags<FlagSet>();

  const options = useMemo(() => {
    if (enableJiraExports) {
      return OPTIONS;
    }
    return OPTIONS.filter((option) => option.key !== 'jira');
  }, [enableJiraExports]);

  const selectedDestination = options.find((option) => option.key === value);

  const handleValueChange = (val: string) => {
    onChange?.({ target: { name, value: val } });
  };

  const handleBlur = () => {
    onBlur?.({ target: { name } });
  };

  return (
    <Select
      name={name}
      value={value}
      onValueChange={handleValueChange}
      {...props}
    >
      <SelectTrigger onBlur={handleBlur} id={id} className="max-w-form">
        <SelectValue
          onChange={onChange}
          placeholder="Select an Export Destination"
        >
          {selectedDestination && (
            <Destination label={selectedDestination.label} />
          )}
        </SelectValue>
        <SelectContent>
          {options.map(({ key, label }) => (
            <SelectItem key={key} value={key}>
              {label}
            </SelectItem>
          ))}
        </SelectContent>
      </SelectTrigger>
    </Select>
  );
}
